import { ReactNode, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { API_ENDPOINTS } from "@/constants/api";
import useUserStore from "@/features/auth/store/useUserStore";
import { useCurrentWorkspaceStore } from "@/features/workspaces/store/useCurrentWorkspaceStore";
import { axiosApiInstance } from "@/libs/axios";
import AppLoader from "@/components/AppLoader";
import PATHS from "@/routes/paths";
import { GetUserDataResponse } from "@/types";

export interface GetUserDataWrapperProps {
  children: ReactNode;
}

const GetUserDataWrapper = ({ children }: GetUserDataWrapperProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const { pathname } = useLocation();
  const { shouldReload, setShouldReload, setRedirectPath } = useUserStore();
  const { userId: storedUserId, setUserId, resetWorkspace } = useCurrentWorkspaceStore();

  const setUser = useUserStore((state) => state.setUser);
  const getUser = async () => {
    try {
      setIsLoading(true);
      const {
        data: { data: userData },
      } = await axiosApiInstance.get<{
        data: GetUserDataResponse;
      }>(API_ENDPOINTS.AUTH.USER_DATA);

      const {
        avatar_url,
        first_name,
        full_name,
        id,
        is_onboarded,
        last_name,
        role,
        permissions,
        special_role_checks,
        timezone,
      } = userData;

      setUser({
        avatarUrl: avatar_url,
        firstName: first_name,
        fullName: full_name,
        id,
        isOnboarded: is_onboarded,
        lastName: last_name,
        role,
        permissions,
        specialRoleChecks: special_role_checks,
        timezone,
      });

      // Safeguard: Check if user changed accounts
      if (storedUserId && storedUserId !== id) {
        // User changed accounts, reset workspace data
        resetWorkspace();
        console.log("User account changed, workspace data reset");
      }

      // Update stored user ID
      setUserId(id);

      console.log(userData);
    } catch (error) {
    } finally {
      setIsLoading(false);
      setIsInitialLoad(false);
      if (shouldReload) {
        setShouldReload(false);
      }
    }
  };

  useEffect(() => {
    if (isInitialLoad || pathname === PATHS.REDIRECT || shouldReload) {
      if (isInitialLoad) {
        setRedirectPath(pathname);
      }
      getUser();
    }
  }, [pathname, shouldReload]);

  if (isLoading) {
    return <AppLoader />;
  }

  return children;
};

export default GetUserDataWrapper;
